/**
 * Utility functions for PDF generation
 */

/**
 * Loads a script dynamically and returns a promise that resolves when the script is loaded
 * @param {string} src - The URL of the script to load
 * @returns {Promise} A promise that resolves when the script is loaded
 */
const loadScript = (src) => new Promise(resolve => {
  const s = document.createElement('script');
  s.src = src;
  s.onload = resolve;
  document.head.appendChild(s);
});

/**
 * Ensures all required PDF dependencies are loaded
 * @returns {Promise} A promise that resolves when all dependencies are loaded
 */
const ensureDependencies = async () => {
  if (!window.html2canvas) {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
  }
  if (!window.jspdf) {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  }

  // Load Outfit font if not already loaded
  if (!document.querySelector('link[href*="Outfit"]')) {
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;700;900&display=swap';
    document.head.appendChild(fontLink);
  }
};

/**
 * Converts pixels to millimeters
 * @param {number} px - The pixel value to convert
 * @returns {number} The value in millimeters
 */
const pxToMm = px => (px * 25.4) / 96;

/**
 * Creates a PDF document with standard settings
 * @returns {Object} The jsPDF instance and page size in mm
 */
const createPDFDocument = () => {
  const { jsPDF } = window.jspdf;
  const pageSizeMM = pxToMm(1080);
  
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pageSizeMM, pageSizeMM]
  });

  return { doc, pageSizeMM };
};

/**
 * Prepares the PDF environment by loading dependencies and creating the document
 * @returns {Promise<Object>} An object containing the PDF document and page size
 */
const preparePDFEnvironment = async () => {
  await ensureDependencies();
  return createPDFDocument();
};

export {
  preparePDFEnvironment,
  pxToMm,
  loadScript,
  ensureDependencies,
  createPDFDocument
}; 